import { get, map, isEmpty, size } from 'lodash-es';
import uuidv4 from 'uuid/v4';
import { addAsteriskMandatoryField, checkIsNull, getByField } from '../../Utils/utils';
import { sortBySequence, parseToJson } from './QuestionnaireBlock.utils';
import { RoleField } from './QuestionnaireBlock.enum';

/**
 *
 * @param uuid
 * @param questions
 */
const createGroups = (uuid, questions) => {
  try {
    // get questions item
    const getQuestionItem = questionsItem => {
      const result = {
        code: get(questionsItem, 'code'),
        sequence: get(questionsItem, 'sequence'),
      };
      return result;
    };

    const newGroup = {
      id: uuid,
      title: '',
      description: '',
      tooltip: '',
      sequence: 1,
      questions: map(questions, questionsItem => getQuestionItem(questionsItem)),
    };
    return [newGroup];
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(`createGroups: ${e}`);
  }
  return undefined;
};

/**
 * questionnaire prop transform
 * @param questionnaire
 */
const questionnaireFirstTransform = ({ questionnaire }) => {
  try {
    // handle groups
    const handleGroups = ({ groups, questions }) => {
      let newGroups = groups;
      if (isEmpty(groups)) {
        newGroups = createGroups(uuidv4(), questions);
      }
      return newGroups;
    };
    // check remove part
    const checkRemovePart = ({ questions }) => size(questions) <= 0;

    // get parts item
    const getPartItem = partsItem => {
      // get part
      const remove = checkRemovePart({ questions: get(partsItem, 'questions') });
      const groups = handleGroups({ groups: get(partsItem, 'groups'), questions: get(partsItem, 'questions') });

      // return part
      const part = {
        ...partsItem,
        remove,
        groups,
      };
      return part;
    };

    const parts = map(get(questionnaire, 'parts'), partsItem => getPartItem(partsItem));
    // return questionnaire
    const result = {
      ...questionnaire,
      parts,
    };
    return result;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
  }
  return undefined;
};

/**
 * questionnaire transform
 * @param questionnaire
 */
const questionnaireTransform = ({ questionnaire }) => {
  try {
    // get part Item
    const getPartItem = partsItem => {
      const result = {
        id: get(partsItem, 'id'),
        sequence: get(partsItem, 'sequence'),
      };
      return result;
    };

    const result = {
      code: get(questionnaire, 'code'),
      title: get(questionnaire, 'title'),
      description: get(questionnaire, 'description'),
      refParts: map(get(questionnaire, 'parts'), partsItem => getPartItem(partsItem)).sort(sortBySequence),
    };

    return result;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(`questionnaireTransform ${e}`);
  }
  return undefined;
};

/**
 * parts transform
 * @param questionnaire
 */
const partsTransform = ({ questionnaire }) => {
  try {
    // get group item
    const getGroupItem = groupsItem => {
      const result = {
        id: get(groupsItem, 'id'), // todo add id in group 'web service'
        sequence: get(groupsItem, 'sequence'),
      };
      return result;
    };

    // get part item
    const getPartItem = partsItem => {
      const result = {
        id: get(partsItem, 'id'),
        title: get(partsItem, 'title'),
        displayTitle: get(partsItem, 'displayTitle'),
        description: get(partsItem, 'description'),
        // sequence: get(partsItem, 'sequence'),
        show: true,
        remove: get(partsItem, 'remove'),
        refGroups: map(get(partsItem, 'groups'), groupsItem => getGroupItem(groupsItem)).sort(sortBySequence),
      };
      return result;
    };

    const result = map(get(questionnaire, 'parts'), partsItem => getPartItem(partsItem));
    return result;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(`partsTransform ${e}`);
  }
  return undefined;
};

/**
 * groups transform
 * @param questionnaire
 */
const groupsTransform = ({ questionnaire }) => {
  try {
    // handle sequence
    const handleSequence = ({ sequence, questions, code }) => {
      let newSequence = sequence;
      if (checkIsNull(sequence)) {
        // if sequence questions not defined in groups.
        const question = getByField('code')({ data: questions, value: code });
        newSequence = get(question, 'sequence');
      }
      return newSequence;
    };

    // get question item
    const getQuestionItem = ({ question, questionsPart }) => {
      const sequence = handleSequence({
        sequence: get(question, 'sequence'),
        questions: questionsPart,
        code: get(question, 'code'),
      });
      const result = {
        code: get(question, 'code'),
        sequence,
      };

      return result;
    };

    // handle tooltip
    const handleTooltip = tooltip => {
      let result;
      if (!checkIsNull(tooltip)) {
        result = false;
      }
      return result;
    };

    // get group item
    const getGroupItem = ({ group, questionsPart }) => {
      const questions = get(group, 'questions');
      const result = {
        /**  groups property * */
        id: get(group, 'id'), // todo add id in group 'web service'
        title: get(group, 'title'),
        description: get(group, 'description'),
        tooltip: get(group, 'tooltip'),
        showTooltip: handleTooltip(get(group, 'tooltip')),
        show: true,
        refQuestions: map(questions, question => getQuestionItem({ question, questionsPart })).sort(sortBySequence),
        // sequence: get(groupsItem, 'sequence'),
      };
      return result;
    };

    // get part item
    const getPartItem = partsItem => {
      const questionsPart = get(partsItem, 'questions');
      const result = map(get(partsItem, 'groups'), group => getGroupItem({ group, questionsPart }));
      return result;
    };

    const result = map(get(questionnaire, 'parts'), partsItem => getPartItem(partsItem)).flat();

    return result;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
  }
  return undefined;
};

/**
 * questions transform
 * @param questionnaire
 */
const questionsTransform = ({ questionnaire }) => {
  try {
    // handle tooltip
    const handleShowHelp = help => {
      let result;
      if (!checkIsNull(help)) {
        result = false;
      }
      return result;
    };

    // handle digits
    const handleDigits = digits => {
      let newDigits;
      if (!Number.isNaN(Number(digits))) {
        newDigits = Number(digits);
      }
      return newDigits;
    };

    // handle value
    const handleValue = ({ value, defaultLocal }) => {
      let newValue = defaultLocal;
      if (!checkIsNull(value)) {
        newValue = value;
      }
      return newValue;
    };

    // get question item
    const getQuestionItem = questionsItem => {
      const name = addAsteriskMandatoryField(
        get(questionsItem, 'label') || get(questionsItem, 'name'),
        get(questionsItem, 'validation')
      );
      const digits = handleDigits(get(questionsItem, 'digits'));
      const value = handleValue({ value: get(questionsItem, 'value'), defaultLocal: get(questionsItem, 'default') });
      const showHelp = handleShowHelp(get(questionsItem, 'help'));

      const getRole = options => {
        const optionsParse = parseToJson(options);
        const role = get(optionsParse, 'role', RoleField.WRITE);
        return role;
      };
      const role = getRole(get(questionsItem, 'custom_resources.options', '{}'));
      const disabled = role === RoleField.READ;
      const hidden = role === RoleField.HIDDEN;

      const help = get(questionsItem, 'help', get(questionsItem, 'custom_resources.helper', ''));

      const result = {
        ...questionsItem,
        name,
        init: get(questionsItem, 'default'),
        digits,
        value,
        show: true,
        hidden,
        disabled,
        webInput: get(questionsItem, 'custom_resources.web_input'),
        help,
        showHelp,
      };
      return result;
    };

    // get part item
    const getPartItem = partsItem => {
      const result = map(get(partsItem, 'questions'), questionsItem => getQuestionItem(questionsItem));

      return result;
    };

    const result = map(get(questionnaire, 'parts'), partsItem => getPartItem(partsItem)).flat();

    return result;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
  }
  return undefined;
};

/**
 * questions ordered transform
 * @param parts
 * @param groups
 * @param questions
 */
const questionsOrderedTransform = ({ parts, groups, questions }) => {
  try {
    // get group item
    const getGroupItem = refGroupsItem => {
      const group = getByField('id')({ data: groups, value: get(refGroupsItem, 'id') });
      const result = map(get(group, 'refQuestions'), refQuestionsItem =>
        getByField('code')({ data: questions, value: get(refQuestionsItem, 'code') })
      );
      return result;
    };

    // get part item
    const getPartItem = partsItem => {
      const result = map(partsItem.refGroups, refGroupsItem => getGroupItem(refGroupsItem)).flat();

      return result;
    };

    return map(parts, partsItem => getPartItem(partsItem)).flat();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
  }
  return undefined;
};

export const initializeState = ({ questionnaire }) => {
  try {
    const questionnaireTransformed = questionnaireFirstTransform({ questionnaire });
    const parts = partsTransform({ questionnaire: questionnaireTransformed });
    const groups = groupsTransform({ questionnaire: questionnaireTransformed });
    const questions = questionsTransform({ questionnaire: questionnaireTransformed });

    const state = {
      data: {
        questionnaire: questionnaireTransform({ questionnaire: questionnaireTransformed }),
        parts,
        groups,
        questions: questionsOrderedTransform({ parts, groups, questions }),
      },
      codeFirstError: undefined,
    };

    return state;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(`initializeState: ${e}`);
  }
  return undefined;
};
